import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Pagination } from "app/pages/component/Pagination";
import { getBiddings } from "sr/utils/api/corporate/biddings";
import { Bidding } from "sr/models/Bidding";
import { getUserDetails } from "sr/helpers/functions/getUserDetails";
import { ListingHeader } from "sr/layout/common/ListingHeader";
import { TabType } from "sr/models/Requirement";
import { StatusPill } from 'sr/helpers/ui-components/StatusPill'
import ContentWithLoader from 'sr/layout/common/ContentLoader'
import { MetaBox } from 'sr/helpers/ui-components/MetaBox'
import { useIntl } from 'react-intl'
import { getPricingType } from 'sr/utils/api/masterData/getPricingType'



export const BiddingListing = () => {
  const intl = useIntl();
  const [pricingLabelMap, setPricingLabelMap] = useState<Record<string, string>>({});
  useEffect(() => {
    const load = async () => {
      try {
        const p = await getPricingType();
        if (p?.success && Array.isArray(p.data)) {
          const m: Record<string, string> = {};
          p.data.forEach((i: any) => (m[i.value] = i.label));
          setPricingLabelMap(m);
        }
      } catch {}
    };
    load();
  }, []);

  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [tab, setTab] = useState<TabType>("Open");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("-date");
  const [loading, setLoading] = useState(false);
  const [biddings, setBiddings] = useState<Bidding[]>([]);

  const corporateId = getUserDetails()?.user_id;

  const fetchData = async () => {
    setLoading(true);
    try {
      const params: any = { page, limit };
      if (corporateId) params.corporate_id = corporateId;
      const res = await getBiddings(params);
      if (res && res.success) {
        setBiddings(res.data || []);
      } else {
        setBiddings([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, limit, corporateId]);

  const filteredSorted = useMemo(() => {
    const q = (search || "").toLowerCase();

    const closedStatuses = ["rejected", "withdrawn", "closed", "completed"];
    const isClosedLike = (status?: string) =>
      closedStatuses.includes(String(status || "").toLowerCase());

    const filtered = biddings.filter((b: any) => {
      const inTab =
        tab === "Open" ? !isClosedLike(b.status) : isClosedLike(b.status);

      const reqTitle = (
        b?.requirement_id?.title || ""
      )
        .toString()
        .toLowerCase();
      const corpName = (
        b?.corporate_id?.corporate_name || ""
      )
        .toString()
        .toLowerCase();

      const matches =
        reqTitle.includes(q) ||
        corpName.includes(q) ||
        String(b.bid_amount || "").toLowerCase().includes(q) ||
        String(b.status || "").toLowerCase().includes(q);

      return inTab && matches;
    });

    const sorted = [...filtered].sort((a, b) => {
      if (sortBy === "amount")
        return Number(a.bid_amount || 0) - Number(b.bid_amount || 0);
      if (sortBy === "-amount")
        return Number(b.bid_amount || 0) - Number(a.bid_amount || 0);
      if (sortBy === "date")
        return (
          new Date(a.updatedAt || a.createdAt || 0).getTime() -
          new Date(b.updatedAt || b.createdAt || 0).getTime()
        );
      if (sortBy === "-date")
        return (
          new Date(b.updatedAt || b.createdAt || 0).getTime() -
          new Date(a.updatedAt || a.createdAt || 0).getTime()
        );
      return 0;
    });

    return sorted;
  }, [biddings, search, sortBy, tab]);

  const totalPage = Math.max(1, Math.ceil(filteredSorted.length / limit));
  const start = (page - 1) * limit;
  const paged = filteredSorted.slice(start, start + limit);

  return (
    <div className="py-3">
      <ListingHeader
        title={<span>Biddings</span>}
        tabs={[
          { key: 'Open', label: <>Open ({biddings.filter((b: any) => !['rejected', 'withdrawn', 'closed', 'completed'].includes(String(b.status || '').toLowerCase())).length})</> },
          { key: 'Closed', label: <>Closed ({biddings.filter((b: any) => ['rejected', 'withdrawn', 'closed', 'completed'].includes(String(b.status || '').toLowerCase())).length})</> },
        ]}
        activeKey={tab}
        onTabChange={(k) => setTab(k as any)}
        search={search}
        onSearchChange={setSearch}
        sortBy={sortBy}
        onSortChange={setSortBy}
        primaryButton={<></>}
        searchPlaceholderId="SEARCH.BIDDINGS.PLACEHOLDER"
      />

      <ContentWithLoader
        loading={loading}
        content={
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="p-6 flex flex-col rounded-[20px] mb-6 border animate-pulse">
                <div className="flex justify-between items-center mb-3">
                  <div className="h-5 w-40 bg-gray-200 rounded" />
                  <div className="h-4 w-16 bg-gray-200 rounded-full" />
                </div>
                <div className="h-3 w-3/4 bg-gray-200 rounded mb-3" />
                <div className="flex gap-3">
                  <div className="h-10 w-40 bg-gray-200 rounded" />
                  <div className="h-10 w-28 bg-gray-200 rounded" />
                </div>
              </div>
            ))}
          </div>
        }
      >
        <div className="mt-5 space-y-4">
          {paged.length > 0 ? (
            paged.map((b: any) => (
              <Link
                key={b.id}
                to={`/corporate/biddings/${b.id}`}
                className="p-6 flex flex-col gap-4 transition-colors hover:bg-gray-50/50 rounded-[20px] mb-6 border border-2xl group cursor-pointer"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-[#0078ae] group-hover:underline">
                      {b?.requirement_id?.title || "—"}
                    </h3>
                  </div>
                  <StatusPill status={b.status} />
                </div>

                {b?.description && (
                  <p className="text-sm text-gray-600">{b.description}</p>
                )}

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  <MetaBox label="Amount" value={String(b.bid_amount ?? '—')} />
                  <MetaBox label="Pricing" value={
                    (b as any)?.pricing_type
                      ? intl.formatMessage({ id: pricingLabelMap[String((b as any).pricing_type)] || String((b as any).pricing_type), defaultMessage: String((b as any).pricing_type) })
                      : '—'
                  } />
                  <MetaBox label="Created" value={b.createdAt ? new Date(b.createdAt).toLocaleDateString() : '—'} />
                </div>
              </Link>
            ))
          ) : (
            <div className="text-center py-10 text-gray-500">
              No biddings found.
            </div>
          )}
        </div>
      </ContentWithLoader>

      {!loading && filteredSorted.length > 0 && (
        <div className="mt-4">
          <Pagination page={page} totalPage={totalPage} setPage={setPage} />
        </div>
      )}
    </div>
  );
};

export default BiddingListing;
